import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Upload } from 'lucide-react';
import './GenericFunFactsManager.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGES = {
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano'
};

function GenericFunFactsManager() {
  const navigate = useNavigate();
  const [facts, setFacts] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [newFact, setNewFact] = useState('');
  const [bulkFacts, setBulkFacts] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchFacts();
  }, [selectedLanguage]);

  const fetchFacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/generic-fun-facts`, {
        params: { language: selectedLanguage }
      });
      setFacts(response.data.facts || []);
    } catch (error) {
      console.error('Error fetching fun facts:', error);
      toast.error('Fehler beim Laden der Fun Facts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFact = async () => {
    if (!newFact.trim()) {
      toast.error('Bitte einen Fun Fact eingeben');
      return;
    }

    try {
      await axios.post(`${API}/api/admin/generic-fun-facts`, {
        language: selectedLanguage,
        fact: newFact.trim()
      });
      
      toast.success('✅ Fun Fact hinzugefügt!');
      setNewFact('');
      await fetchFacts();
    } catch (error) {
      console.error('Error adding fun fact:', error);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleBulkAddFacts = async () => {
    if (!bulkFacts.trim()) {
      toast.error('Bitte Fun Facts eingeben (eine pro Zeile)');
      return;
    }

    const factsArray = bulkFacts
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (factsArray.length === 0) {
      toast.error('Keine gültigen Fun Facts gefunden');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/generic-fun-facts/bulk`, {
        language: selectedLanguage,
        facts: factsArray
      });
      
      toast.success(`✅ ${factsArray.length} Fun Facts hinzugefügt!`);
      setBulkFacts('');
      await fetchFacts();
    } catch (error) {
      console.error('Error bulk adding fun facts:', error);
      toast.error('Fehler beim Hinzufügen');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFact = async (factId) => {
    if (!window.confirm('Diesen Fun Fact wirklich löschen?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/generic-fun-facts/${factId}`);
      toast.success('✅ Fun Fact gelöscht!');
      await fetchFacts();
    } catch (error) {
      console.error('Error deleting fun fact:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div className="fun-facts-manager">
      <Button 
        variant="ghost"
        onClick={() => navigate('/admin')}
        className="mb-4"
      >
        <ArrowLeft size={20} className="mr-2" /> Zurück
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>💡 Generic Fun Facts Manager</CardTitle>
          <CardDescription>
            Verwalte generische Fun Facts pro Sprache
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Language Selector */}
          <div className="mb-6">
            <Label>Sprache</Label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LANGUAGES).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="single">
            <TabsList className="mb-4">
              <TabsTrigger value="single">Einzeln</TabsTrigger>
              <TabsTrigger value="bulk">Mehrere (Bulk)</TabsTrigger>
              <TabsTrigger value="list">Liste ({facts.length})</TabsTrigger>
            </TabsList>

            {/* Single Tab */}
            <TabsContent value="single">
              <div className="space-y-4">
                <div>
                  <Label>Fun Fact ({LANGUAGES[selectedLanguage]})</Label>
                  <Textarea
                    value={newFact}
                    onChange={(e) => setNewFact(e.target.value)}
                    placeholder="Wusstest du, dass..."
                    rows={4}
                    className="mt-2"
                  />
                </div>
                <Button 
                  onClick={handleAddFact}
                  disabled={!newFact.trim() || loading}
                >
                  <Plus size={18} className="mr-2" />
                  Hinzufügen
                </Button>
              </div>
            </TabsContent>

            {/* Bulk Tab */}
            <TabsContent value="bulk">
              <div className="space-y-4">
                <div>
                  <Label>Mehrere Fun Facts (eine pro Zeile)</Label>
                  <Textarea
                    value={bulkFacts}
                    onChange={(e) => setBulkFacts(e.target.value)}
                    placeholder="Wusstest du, dass Wasser nass ist?
Bananen sind krumm.
Die Erde ist rund."
                    rows={12}
                    className="mt-2 font-mono"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    💡 Eine Zeile = Ein Fun Fact. Leere Zeilen werden ignoriert.
                  </p>
                </div>
                <Button 
                  onClick={handleBulkAddFacts}
                  disabled={!bulkFacts.trim() || loading}
                >
                  <Upload size={18} className="mr-2" />
                  {loading ? 'Hinzufügen...' : 'Alle hinzufügen'}
                </Button>
              </div>
            </TabsContent>

            {/* List Tab */}
            <TabsContent value="list">
              {loading ? (
                <div className="text-center py-8">Lädt...</div>
              ) : facts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Noch keine Fun Facts für {LANGUAGES[selectedLanguage]}
                </div>
              ) : (
                <div className="space-y-2">
                  {facts.map((fact, index) => (
                    <div key={fact.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-500 font-mono">{index + 1}.</span>
                      <p className="flex-1">{fact.fact}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteFact(fact.id)}
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

export default GenericFunFactsManager;
